// The opened world row (Ben, Oct 19 2026): every city in that world with something idle — the City Overview's queue rows
// (build, Barracks/Stables/Workshop, Forge, Academy; a building not built yet isn't counted). One village/get per city, only
// while the row is open, at most READ_AHEAD at a time (a 100-city world fills in as the reads come back). The reads share the
// city screens' cache, so a row opened once this session shows its idle count closed too.
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { Village } from "@/api/types";
import { keys } from "@/state/queries";
import { overviewQueues } from "@/game/overview";

export const READ_AHEAD = 6;

export interface IdleCity {
  vid: number;
  name: string;
  coords: string;
  /** "Build 0/5", "Barracks", "Forge", … */
  idle: string[];
}

/** The idle queue rows of one city, as the chips say them. */
export function idleOf(v: Village, now: number): string[] {
  return overviewQueues(v, now).filter((r) => !r.busy).map((r) => (r.kind === "build" ? `Build ${r.count ?? ""}`.trim() : r.label));
}

export function useWorldQueues(wid: number, vids: number[], open: boolean, now: number) {
  const qs = useQueries({ queries: vids.map((vid) => ({ queryKey: keys.village(wid, vid), queryFn: () => api.village.get({ wid, vid }), staleTime: 60_000, enabled: false })) });
  const settled = qs.filter((q) => q.data || q.isError).length;
  // the window: the next READ_AHEAD cities not read yet are let through
  let ahead = 0;
  const allow = qs.map((q) => {
    if (q.data || q.isError) return true;
    ahead += 1;
    return ahead <= READ_AHEAD;
  });
  useQueries({
    queries: vids.map((vid, i) => ({
      queryKey: keys.village(wid, vid),
      queryFn: () => api.village.get({ wid, vid }),
      staleTime: 60_000,
      enabled: open && allow[i],
    })),
  });
  const idle: IdleCity[] = [];
  qs.forEach((q, i) => {
    const v = q.data;
    if (!v) return;
    const chips = idleOf(v, now);
    if (!chips.length) return;
    idle.push({ vid: vids[i], name: v.village?.city_name ?? `City ${vids[i]}`, coords: String(v.village?.coords ?? ""), idle: chips });
  });
  const read = qs.filter((q) => q.data).length;
  return { idle, read, total: vids.length, complete: settled >= vids.length && vids.length > 0, loading: open && settled < vids.length, failed: qs.filter((q) => q.isError).length };
}
